/**
 * Edit Session Hook
 * 
 * Manages document edit sessions for concurrent edit detection.
 * - Starts session when entering edit mode
 * - Sends heartbeats to keep session alive
 * - Ends session when exiting edit mode or closing tab
 * - Warns user if other editors are detected
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { logger } from '../utils/logger';

// Generate unique session ID per browser tab
const generateSessionId = (): string => {
  // Use sessionStorage to persist across page reloads within the same tab
  let sessionId = sessionStorage.getItem('editSessionId');
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('editSessionId', sessionId);
  }
  return sessionId;
};

interface OtherEditor {
  userId: string;
  userEmail: string;
  userName: string;
  startedAt: string;
  lastHeartbeat: string;
}

interface UseEditSessionProps {
  projectId: string | undefined;
  documentNumber: string | undefined;
  isEditing: boolean;
  onConflictDetected?: (editors: OtherEditor[]) => void;
}

interface UseEditSessionReturn {
  otherEditors: OtherEditor[];
  hasConflict: boolean;
  sessionActive: boolean;
  error: string | null;
}

// Heartbeat interval: 30 seconds (session expires after 2 minutes without heartbeat)
const HEARTBEAT_INTERVAL = 30000;

export function useEditSession({
  projectId,
  documentNumber,
  isEditing,
  onConflictDetected
}: UseEditSessionProps): UseEditSessionReturn {
  const [otherEditors, setOtherEditors] = useState<OtherEditor[]>([]);
  const [hasConflict, setHasConflict] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string>(generateSessionId());
  
  // Start edit session
  const startSession = useCallback(async () => {
    if (!projectId || !documentNumber) return;
    
    try {
      logger.debug('[EditSession] Starting session for', projectId, documentNumber);
      
      const response = await api.post('/edit-sessions/start', {
        projectId,
        documentNumber,
        sessionId: sessionIdRef.current
      });
      
      const data = response.data.data || response.data;
      
      setSessionActive(true);
      setOtherEditors(data.otherEditors || []);
      setHasConflict(data.hasConflict || false);
      setError(null);
      
      if (data.hasConflict && onConflictDetected) {
        onConflictDetected(data.otherEditors);
      }
      
      logger.debug('[EditSession] Session started, other editors:', data.otherEditors?.length || 0);
      
    } catch (err: unknown) {
      logger.error('[EditSession] Failed to start session:', err);
      setError(err instanceof Error ? err.message : 'Failed to start edit session');
    }
  }, [projectId, documentNumber, onConflictDetected]);
  
  // Send heartbeat
  const sendHeartbeat = useCallback(async () => {
    if (!projectId || !documentNumber || !sessionActive) return;
    
    try {
      const response = await api.post('/edit-sessions/heartbeat', {
        projectId,
        documentNumber,
        sessionId: sessionIdRef.current
      });
      
      const data = response.data.data || response.data;
      
      setOtherEditors(data.otherEditors || []);
      setHasConflict(data.hasConflict || false);
      
      if (data.hasConflict && onConflictDetected) {
        onConflictDetected(data.otherEditors);
      }
      
    } catch (err: unknown) {
      logger.error('[EditSession] Heartbeat failed:', err);
      // Don't set error for heartbeat failures - session might still be valid
    }
  }, [projectId, documentNumber, sessionActive, onConflictDetected]);
  
  // End edit session
  const endSession = useCallback(async () => {
    if (!projectId || !documentNumber) return;
    
    try {
      logger.debug('[EditSession] Ending session for', projectId, documentNumber);
      
      await api.post('/edit-sessions/end', {
        projectId,
        documentNumber,
        sessionId: sessionIdRef.current
      });
      
      setSessionActive(false);
      setOtherEditors([]);
      setHasConflict(false);
      
      logger.debug('[EditSession] Session ended');
      
    } catch (err: unknown) {
      logger.error('[EditSession] Failed to end session:', err);
      // Don't block user from exiting edit mode
    }
  }, [projectId, documentNumber]);
  
  // Start/end session based on isEditing
  useEffect(() => {
    if (isEditing && projectId && documentNumber) {
      startSession();
      
      // Start heartbeat interval
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
      
    } else if (!isEditing && sessionActive) {
      endSession();
      
      // Stop heartbeat interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    }
    
    return () => {
      // Cleanup on unmount
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [isEditing, projectId, documentNumber, startSession, endSession, sendHeartbeat, sessionActive]);
  
  // End session on tab close/navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionActive && projectId && documentNumber) {
        // Use sendBeacon for reliable delivery during page unload
        const data = JSON.stringify({
          projectId,
          documentNumber,
          sessionId: sessionIdRef.current
        });
        
        // Try sendBeacon first (more reliable during unload)
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const beaconSent = navigator.sendBeacon(
          `${apiUrl}/v1/edit-sessions/end`,
          new Blob([data], { type: 'application/json' })
        );
        
        if (!beaconSent) {
          // Fallback to sync XHR (less reliable but worth trying)
          logger.debug('[EditSession] sendBeacon failed, trying sync XHR');
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionActive, projectId, documentNumber]);
  
  return {
    otherEditors,
    hasConflict,
    sessionActive,
    error
  };
}
